import { useState } from 'react'
import { toast } from 'sonner'
import { usePejabatDropdown, useSubmitBukuTamu } from '../hooks/use-buku-tamu'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import type { PublicBukuTamuCreateRequestItem } from '@tatatemplate/shared-lib'
import { Plus, Trash2, Loader2, Send } from 'lucide-react'

const emptyItem = (): PublicBukuTamuCreateRequestItem => ({
  // `id` WAJIB dan ber-format uuid menurut kontrak — dibuat KLIEN, bukan server.
  id: crypto.randomUUID(),
  nama: '',
  alamat: '',
  instansi: '',
  tujuanPejabatId: '',
  keperluan: '',
})

export default function BukuTamuPage() {
  const [items, setItems] = useState<PublicBukuTamuCreateRequestItem[]>([emptyItem()])
  const [errors, setErrors] = useState<Record<string, string>[]>([{}])
  const { data: pejabatList, isLoading: pejabatLoading } = usePejabatDropdown()
  const submitMutation = useSubmitBukuTamu()
  const [submitted, setSubmitted] = useState(false)

  const updateItem = (index: number, field: keyof PublicBukuTamuCreateRequestItem, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
    setErrors((prev) => prev.map((err, i) => (i === index ? { ...err, [field]: '' } : err)))
  }

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()])
    setErrors((prev) => [...prev, {}])
  }

  const removeItem = (index: number) => {
    if (items.length <= 1) return
    setItems((prev) => prev.filter((_, i) => i !== index))
    setErrors((prev) => prev.filter((_, i) => i !== index))
  }

  const validate = (): boolean => {
    const newErrors = items.map((item) => {
      const err: Record<string, string> = {}
      if (!item.nama.trim()) err.nama = 'Nama wajib diisi'
      if (!item.alamat.trim()) err.alamat = 'Alamat wajib diisi'
      if (!item.tujuanPejabatId) err.tujuanPejabatId = 'Tujuan kunjungan wajib dipilih'
      if (!item.keperluan.trim()) err.keperluan = 'Keperluan wajib diisi'
      return err
    })
    setErrors(newErrors)
    return newErrors.every((err) => Object.keys(err).length === 0)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const payload = items.map((item) => ({
      id: item.id,
      nama: item.nama.trim(),
      alamat: item.alamat.trim(),
      instansi: item.instansi?.trim() || undefined,
      tujuanPejabatId: item.tujuanPejabatId,
      keperluan: item.keperluan.trim(),
    }))

    // orval membungkus request body sebagai `{ data }`.
    submitMutation.mutate({ data: payload }, {
      onSuccess: () => {
        toast.success(`${payload.length} data buku tamu berhasil dikirim`)
        setSubmitted(true)
        setItems([emptyItem()])
        setErrors([{}])
      },
      onError: (error) => {
        toast.error(error.message || 'Gagal mengirim data buku tamu')
      },
    })
  }

  const resetForm = () => {
    setSubmitted(false)
    setItems([emptyItem()])
    setErrors([{}])
  }

  if (submitted) {
    return (
      <div className="container mx-auto max-w-2xl py-12 px-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-center">Terima Kasih!</h2>
            <p className="text-muted-foreground text-center">
              Data buku tamu berhasil dikirim. Terima kasih telah berkunjung.
            </p>
            <Button onClick={resetForm}>Isi Buku Tamu Lagi</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-3xl py-12 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold">Buku Tamu Digital</h1>
        <p className="text-muted-foreground mt-2">
          Silakan isi data kunjungan Anda di bawah ini.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {items.map((item, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Tamu #{index + 1}</CardTitle>
                <CardDescription>Lengkapi data tamu di bawah ini.</CardDescription>
              </div>
              {items.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeItem(index)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Hapus
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Nama <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Nama lengkap"
                  value={item.nama}
                  onChange={(e) => updateItem(index, 'nama', e.target.value)}
                />
                {errors[index]?.nama && (
                  <p className="text-sm text-red-500 mt-1">{errors[index].nama}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Alamat <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-corporate-gray-300 bg-white px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-corporate-blue-500 focus-visible:ring-corporate-blue-200 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Alamat tempat tinggal"
                  value={item.alamat}
                  onChange={(e) => updateItem(index, 'alamat', e.target.value)}
                />
                {errors[index]?.alamat && (
                  <p className="text-sm text-red-500 mt-1">{errors[index].alamat}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Instansi (Opsional)
                </label>
                <Input
                  placeholder="Nama instansi / organisasi"
                  value={item.instansi || ''}
                  onChange={(e) => updateItem(index, 'instansi', e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Tujuan Kunjungan <span className="text-red-500">*</span>
                </label>
                {pejabatLoading ? (
                  <div className="h-9 w-full rounded-md border border-corporate-gray-300 bg-gray-50 animate-pulse" />
                ) : (
                  <select
                    className="flex h-9 w-full rounded-md border border-corporate-gray-300 bg-white px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:border-corporate-blue-500 focus-visible:ring-corporate-blue-200 focus-visible:ring-[3px]"
                    value={item.tujuanPejabatId}
                    onChange={(e) => updateItem(index, 'tujuanPejabatId', e.target.value)}
                  >
                    <option value="">Pilih pejabat yang dituju</option>
                    {pejabatList?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nama} — {p.namaJabatan}
                      </option>
                    ))}
                  </select>
                )}
                {errors[index]?.tujuanPejabatId && (
                  <p className="text-sm text-red-500 mt-1">{errors[index].tujuanPejabatId}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Keperluan <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-corporate-gray-300 bg-white px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-corporate-blue-500 focus-visible:ring-corporate-blue-200 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Jelaskan keperluan kunjungan"
                  value={item.keperluan}
                  onChange={(e) => updateItem(index, 'keperluan', e.target.value)}
                />
                {errors[index]?.keperluan && (
                  <p className="text-sm text-red-500 mt-1">{errors[index].keperluan}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={addItem}
        >
          <Plus className="w-4 h-4 mr-2" />
          Tambah Tamu Lainnya
        </Button>

        <div className="flex justify-end">
          <Button type="submit" disabled={submitMutation.isPending}>
            {submitMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Kirim {items.length > 1 ? `(${items.length} Tamu)` : ''}
          </Button>
        </div>
      </form>
    </div>
  )
}
